/**
 * Workbench field-lock + presence API (Phase 2 — collaborative IDE).
 *
 * Locks are soft, server-authoritative, and TTL-bounded:
 *   POST   /networks/{id}/locks                 { field, ttlSeconds }
 *   GET    /networks/{id}/locks                 { locks: [...] }
 *   DELETE /networks/{id}/locks                 { field }  (force=true via ?force=true)
 *   POST   /networks/{id}/workbench/presence    { kind, cursor? }
 *
 * Channel contract: the API publishes ``FIELD_LOCK_ACQUIRED``/``RELEASED``/
 * ``EXPIRED`` + ``WORKBENCH_PRESENCE_*`` to
 *   ``/default/workbench/{tenantId}/{networkId}``
 * which every concurrent editor subscribes to via ``useWorkbenchChannel``.
 *
 * Only BUILD_ROLES (OWNER / ADMIN / CREATOR) may call these endpoints; the
 * backend 403s other callers, so there is no need for the UI to pre-filter.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function encodePath(s) {
  return encodeURIComponent(String(s || '').trim());
}

async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || data.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Acquire or refresh a soft lock on a specific Workbench field.
 *
 * @param {string} networkId
 * @param {string} field      opaque path ("agent:acme-bot:prompt", "network:name", …)
 * @param {number} [ttlSeconds=120]
 *
 * @returns {Promise<{lock: WorkbenchLock}>}
 * @throws  {Error & { status: 409, data: { lock } }} when another editor owns the live lock
 */
export async function acquireFieldLock(networkId, field, ttlSeconds = 120) {
  return authedFetch(`/networks/${encodePath(networkId)}/locks`, {
    method: 'POST',
    body: JSON.stringify({ field, ttlSeconds }),
  });
}

/**
 * Snapshot every live lock for a network — used by joining editors to
 * hydrate their overlay state without waiting for the next AppSync beacon.
 */
export async function listFieldLocks(networkId) {
  return authedFetch(`/networks/${encodePath(networkId)}/locks`);
}

/**
 * Release a field lock you currently hold. Workspace admins may pass
 * ``force=true`` to break a stuck lock held by a crashed browser session.
 *
 * @param {string} networkId
 * @param {string} field
 * @param {{ force?: boolean }} [opts]
 */
export async function releaseFieldLock(networkId, field, opts = {}) {
  const qs = opts.force ? '?force=true' : '';
  return authedFetch(`/networks/${encodePath(networkId)}/locks${qs}`, {
    method: 'DELETE',
    body: JSON.stringify({ field }),
  });
}

/**
 * Announce Workbench presence. ``kind`` is JOINED / LEFT / TYPING.
 * The ``JOINED`` beacon is the right one to send from the mount effect;
 * subsequent TYPING pings can be published directly via AppSync EventPublish
 * by ``useWorkbenchChannel`` to avoid per-keystroke REST hops.
 */
export async function pingWorkbenchPresence(networkId, kind = 'JOINED', cursor = undefined) {
  return authedFetch(`/networks/${encodePath(networkId)}/workbench/presence`, {
    method: 'POST',
    body: JSON.stringify(cursor ? { kind, cursor } : { kind }),
  });
}

/**
 * @typedef {Object} WorkbenchLock
 * @property {string} lockId
 * @property {string} field
 * @property {string} networkId
 * @property {string} holderSub
 * @property {string} [holderEmail]
 * @property {string} acquiredAt   ISO-8601
 * @property {number} expiresAt    epoch seconds (client compares to Date.now()/1000)
 */
